import React, { useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Palette, Boxes, UserCog, Gauge, Square, Sparkles } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Card } from '../../../components/ui/Card';
import { PRIMARY, SUCCESS, WARNING, DANGER, INFO, XP, NEUTRAL, ROLE_COLORS, MASTERY, MASTERY_THRESHOLDS, RADIUS, SHADOW, BADGE_VARIANTS, MODULE_COLOR_MAP } from '../../../config/designTokens';
// ─── Tab config ──────────────────────────────────────────────────────────────
type TabId = 'colors' | 'modules' | 'roles' | 'mastery' | 'shape';
const TABS: Array<{
  id: TabId;
  label: string;
  icon: React.ReactNode;
}> = [{
  id: 'colors',
  label: 'Kleuren',
  icon: <Palette className="w-4 h-4" data-id="element-6852" data-mp="1iuljyfbs58fn" />
}, {
  id: 'modules',
  label: 'Modules',
  icon: <Boxes className="w-4 h-4" data-id="element-6853" data-mp="1ntgeufn0ung4" />
}, {
  id: 'roles',
  label: 'Rollen',
  icon: <UserCog className="w-4 h-4" data-id="element-6854" data-mp="2c4xrd51k45zv" />
}, {
  id: 'mastery',
  label: 'Mastery',
  icon: <Gauge className="w-4 h-4" data-id="element-6855" data-mp="280gb3fueoatu" />
}, {
  id: 'shape',
  label: 'Vorm',
  icon: <Square className="w-4 h-4" data-id="element-6856" data-mp="233pt2mz1j55s" />
}];
const TAB_DESCRIPTION: Record<TabId, string> = {
  colors: 'Brand- en semantische status-tokens. Map op betekenis, niet op hue — danger is rood, warning amber, los van elkaar.',
  modules: 'Het canonieke kleur-palet per leer-module, inclusief de subtiele per-wereld practice-tint (APP_FEEL §3.1).',
  roles: 'Kleuridentiteit per account-rol — teacher violet, parent emerald, learner oranje, admin amber.',
  mastery: 'De vier beheersings-banden met eigen drempels (75/55/35). Hergebruikt nooit status-kleuren: beheersing ≠ correctheid.',
  shape: 'De getokeniseerde radius- en shadow-schaal plus de badge-varianten die de hele app vormgeven.'
};
// ─── Page ──────────────────────────────────────────────────────────────────
/**
 * DesignSystemPage — dedicated, read-only, LIVE showcase of the design-system
 * SSOT (src/config/designTokens.ts). Every swatch reads its value live from the
 * token export at render time, so the surface can never drift from the running
 * app. Tailwind class strings are rendered as-is (they all live in the token
 * source, so they survive purge); raw hex values use inline styles.
 */
export function DesignSystemPage() {
  const [activeTab, setActiveTab] = useState<TabId>('colors');
  const moduleCount = useMemo(() => Object.keys(MODULE_COLOR_MAP).length, []);
  return <PageLayout title="Design System" subtitle="De design-tokens SSOT — live uit de code gelezen, nooit gekopieerd" actions={<ConnectionBadge status="live" data-id="element-6858" data-mp="k4s8ezmn7vvr" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Design System'
  }]} data-id="element-6857" data-mp="f5p27buxfjm7">
      {/* Summary header */}
      <Card className="p-5 mb-5" data-id="element-6859" data-mp="2ehi3vlsntd59">
        <div className="flex items-start gap-4" data-id="element-6860" data-mp="2om7z78tk5j0i">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0" data-id="element-6861" data-mp="2omzqw8k1awli">
            <Palette className="w-5 h-5" data-id="element-6862" data-mp="2joy87oqiszak" />
          </div>
          <div className="min-w-0" data-id="element-6863" data-mp="2xes3aosmcfsq">
            <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-6864" data-mp="2xftcsotzku8c">
              Design-tokens
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-6865" data-mp="2slyu9oq4alau">
              Eén canonieke bron voor kleur, vorm en module-palet:{' '}
              <code className="font-mono text-[12px] text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6866" data-mp="32cjrp8u21js6">
                src/config/designTokens.ts
              </code>
              . Bevat o.a.{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-6867" data-mp="3ccu4wtg3ar7c">
                {moduleCount}
              </span>{' '}
              module-kleursets. Componenten importeren hier vandaan in plaats
              van kleurklassen te hardcoden.
            </p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={id => setActiveTab(id as TabId)} data-id="element-6868" data-mp="1jenhpourguzc" />

      <p className="text-sm text-gray-500 mb-5" data-id="element-6869" data-mp="1sflmwagxr8ji">{TAB_DESCRIPTION[activeTab]}</p>

      <motion.div key={activeTab} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22
    }} role="tabpanel" id={`tabpanel-${activeTab}`} data-id="element-6870" data-mp="1sdilyve8vv00">
        {activeTab === 'colors' && <ColorsTab data-id="element-6871" data-mp="19oltmx2erckf" />}
        {activeTab === 'modules' && <ModulesTab data-id="element-6872" data-mp="19nsuah5s3pcb" />}
        {activeTab === 'roles' && <RolesTab data-id="element-6873" data-mp="1egdmxfcy7we9" />}
        {activeTab === 'mastery' && <MasteryTab data-id="element-6874" data-mp="1eh55nplg5zn4" />}
        {activeTab === 'shape' && <ShapeTab data-id="element-6875" data-mp="h0zq1ot6k7xk" />}
      </motion.div>
    </PageLayout>;
}
// ─── Colors tab ──────────────────────────────────────────────────────────────
function ColorsTab() {
  const tokens = [{
    name: 'Primary',
    token: PRIMARY,
    note: 'Brand / oranje'
  }, {
    name: 'Success',
    token: SUCCESS,
    note: 'Correct / beheerst'
  }, {
    name: 'Warning',
    token: WARNING,
    note: 'Let op (niet destructief)'
  }, {
    name: 'Danger',
    token: DANGER,
    note: 'Fout / destructief'
  }, {
    name: 'Info',
    token: INFO,
    note: 'Neutraal informatief'
  }, {
    name: 'XP',
    token: XP,
    note: 'Gamification'
  }, {
    name: 'Neutral',
    token: NEUTRAL,
    note: 'Ghost / oppervlak'
  }] as const;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6876" data-mp="3hxim0k9n8sxp">
      {tokens.map(({
      name,
      token,
      note
    }) => <Card key={name} className="p-4" data-id="element-6877" data-mp="3cvktezfcsls3">
          <div className={`h-16 w-full rounded-xl ${token.bg} border border-black/5`} aria-hidden="true" data-id="element-6878" data-mp="38wkbrk8m3p6o" />
          <div className="mt-3" data-id="element-6879" data-mp="33xhj6z9tjbfl">
            <div className="flex items-center justify-between gap-2" data-id="element-6880" data-mp="2u9da6550o3kg">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-6881" data-mp="2u8294d2yh9x1">{name}</h4>
              <span className="text-[11px] text-gray-400" data-id="element-6882" data-mp="2ybqgovrl85ia">{note}</span>
            </div>
            <dl className="mt-2 space-y-1 text-[11px]" data-id="element-6883" data-mp="2kc7l12fyqmmv">
              <SwatchRow label="Fill" cls={token.bg} data-id="element-6884" data-mp="2kdikiq2dl886" />
              <SwatchRow label="Tekst" cls={token.text} data-id="element-6885" data-mp="2pb232263w75x" />
              {'border' in token && <SwatchRow label="Border" cls={token.border} data-id="element-6886" data-mp="3d162ryg6bmrn" />}
            </dl>
          </div>
        </Card>)}
    </div>;
}
function SwatchRow({
  label,
  cls
}: {
  label: string;
  cls: string;
}) {
  return <div className="flex items-center justify-between gap-2" data-id="element-6887" data-mp="337xoku3jnr2v">
      <dt className="text-gray-500" data-id="element-6888" data-mp="388397qbecdbg">{label}</dt>
      <dd data-id="element-6889" data-mp="386s67sprz9le">
        <code className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6890" data-mp="3vyphnipbbzlz">
          {cls}
        </code>
      </dd>
    </div>;
}
// ─── Modules tab ─────────────────────────────────────────────────────────────
function ModulesTab() {
  const entries = Object.entries(MODULE_COLOR_MAP);
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6891" data-mp="1kblvhwxn6mpf">
      {entries.map(([key, set]) => <Card key={key} className="overflow-hidden p-0" data-id="element-6892" data-mp="1y30gg9b40xz4">
          {/* Wereld-tint wash header met fill + hex specimen */}
          <div className="px-4 py-4 border-b border-black/5" style={{
        backgroundColor: set.tint
      }} data-id="element-6893" data-mp="1u2gjp6ug7wdi">
            <div className="flex items-center gap-3" data-id="element-6894" data-mp="11d1a8lifmbjp">
              <span className={`w-9 h-9 rounded-xl ${set.bg} shadow-sm border border-black/5 flex-shrink-0`} aria-hidden="true" data-id="element-6895" data-mp="11ecc2fsbh986" />
              <div className="min-w-0" data-id="element-6896" data-mp="1acemtn1urg8n">
                <code className="block font-mono text-xs text-gray-800 truncate" data-id="element-6897" data-mp="1afiwgzy3746p">
                  {key}
                </code>
                <span className="text-[11px] text-gray-500" data-id="element-6898" data-mp="2lsutorzccojw">
                  tint {set.tint}
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-1 text-[11px]" data-id="element-6899" data-mp="35smoo4s55evz">
            <div className="flex items-center justify-between gap-2" data-id="element-6900" data-mp="3auc0hmn6twl4">
              <span className="text-gray-500" data-id="element-6901" data-mp="3eum4itrf6cxc">Hex (ring)</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-6902" data-mp="3et1e9gc51g5a">
                <span className="w-3 h-3 rounded-full border border-black/10" style={{
              backgroundColor: set.ring
            }} aria-hidden="true" data-id="element-6903" data-mp="3jmnpr56kfdzi" />
                <code className="font-mono text-gray-700" data-id="element-6904" data-mp="2dqzot4pm9t3">{set.ring}</code>
              </span>
            </div>
            <SwatchRow label="Gradient" cls={`${set.from} ${set.to}`} data-id="element-6905" data-mp="6f2fy81p1iom" />
            <SwatchRow label="Tekst" cls={set.text} data-id="element-6906" data-mp="6d837h0kswvm" />
            <SwatchRow label="Light" cls={set.light} data-id="element-6907" data-mp="2465dph44uu48" />
          </div>
        </Card>)}
    </div>;
}
// ─── Roles tab ───────────────────────────────────────────────────────────────
function RolesTab() {
  const ROLE_LABELS: Record<string, string> = {
    teacher: 'Leerkracht',
    parent: 'Ouder',
    learner: 'Leerling',
    admin: 'Beheerder'
  };
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6908" data-mp="1qx3iv4ry4fxt">
      {Object.entries(ROLE_COLORS).map(([role, set]) => <Card key={role} className="p-4" data-id="element-6909" data-mp="1mwkr1aq1z9vm">
          <div className={`rounded-xl border px-4 py-3 ${set.bg} ${set.border}`} data-id="element-6910" data-mp="1mtor5gunab4j">
            <div className="flex items-center gap-3" data-id="element-6911" data-mp="2jjxz1phqct5h">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${set.iconBg} ${set.iconText}`} data-id="element-6912" data-mp="2eo0mzvdi28yi">
                <UserCog className="w-4 h-4" data-id="element-6913" data-mp="2an6z32024u5m" />
              </span>
              <div data-id="element-6914" data-mp="25lrax12l27j0">
                <p className={`text-sm font-semibold ${set.text}`} data-id="element-6915" data-mp="25odgww9tsez4">
                  {ROLE_LABELS[role] ?? role}
                </p>
                <span className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${set.badgeBg} ${set.badgeText}`} data-id="element-6916" data-mp="u7dugha5sud0">
                  {role}
                </span>
              </div>
            </div>
          </div>
          <dl className="mt-3 space-y-1 text-[11px]" data-id="element-6917" data-mp="1n6fs2ebv65sg">
            <SwatchRow label="Achtergrond" cls={set.bg} data-id="element-6918" data-mp="1n88zvwy7qw21" />
            <SwatchRow label="Tekst" cls={set.text} data-id="element-6919" data-mp="1iffur7yu1lpf" />
            <SwatchRow label="Border" cls={set.border} data-id="element-6920" data-mp="1idv0zkkce6sm" />
          </dl>
        </Card>)}
    </div>;
}
// ─── Mastery tab ─────────────────────────────────────────────────────────────
function MasteryTab() {
  const BAND_LABELS: Record<string, string> = {
    strong: 'Sterk beheerst',
    solid: 'Degelijk',
    developing: 'In ontwikkeling',
    attention: 'Aandacht nodig'
  };
  const thresholds = MASTERY_THRESHOLDS as Record<string, number>;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-6921" data-mp="bskbr2983qzn">
      {Object.entries(MASTERY).map(([band, set]) => <Card key={band} className="p-4" data-id="element-6922" data-mp="7rqo89zp0e7a">
          <div className="flex items-center justify-between mb-3" data-id="element-6923" data-mp="3tqv3j5pk4xoz">
            <span className="w-9 h-9 rounded-full border border-black/10" style={{
          backgroundColor: set.ring
        }} aria-hidden="true" data-id="element-6924" data-mp="3ttr1gu1enmdf" />
            <span className="text-sm font-bold text-gray-900 tabular-nums" data-id="element-6925" data-mp="3p0oe53n8195o">
              {band in thresholds ? `≥ ${thresholds[band]}` : '< 35'}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6926" data-mp="3a5qo4hfjovrr">
            {BAND_LABELS[band] ?? band}
          </h4>
          <span className="text-[11px] text-gray-400 capitalize" data-id="element-6927" data-mp="312pl4plz7ksb">{band}</span>
          <dl className="mt-3 space-y-1 text-[11px]" data-id="element-6928" data-mp="314senjmc7w1s">
            <SwatchRow label="Fill" cls={set.bg} data-id="element-6929" data-mp="362tyi6rl7zeb" />
            <div className="flex items-center justify-between gap-2" data-id="element-6930" data-mp="363n8dgluf50o">
              <dt className="text-gray-500" data-id="element-6931" data-mp="zbw0hnx4sddg">Ring (hex)</dt>
              <dd data-id="element-6932" data-mp="14az0uv261vf3">
                <code className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6933" data-mp="149e5j38128g6">
                  {set.ring}
                </code>
              </dd>
            </div>
          </dl>
        </Card>)}
    </div>;
}
// ─── Shape tab ───────────────────────────────────────────────────────────────
function ShapeTab() {
  return <div className="space-y-5" data-id="element-6934" data-mp="swxe3ckc7gsx">
      {/* Radius */}
      <Card className="p-5" data-id="element-6935" data-mp="nxb216g8w6lp">
        <div className="flex items-center gap-2 mb-4" data-id="element-6936" data-mp="ny40lk51vpsr">
          <Square className="w-4 h-4 text-gray-400" data-id="element-6937" data-mp="1lk1fl7k81czy" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6938" data-mp="1llkuouu55i05">Radius-schaal</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-6939" data-mp="1gmrozol3ivaj">
          {Object.entries(RADIUS).map(([name, cls]) => <div key={name} className="text-center" data-id="element-6940" data-mp="16vdlz6txrdcn">
              <div className={`h-16 w-full bg-orange-100 border-2 border-orange-300 ${cls}`} aria-hidden="true" data-id="element-6941" data-mp="16sret8ayizuy" />
              <p className="mt-2 text-xs font-semibold text-gray-700 capitalize" data-id="element-6942" data-mp="1zihu3kobs7sn">
                {name}
              </p>
              <code className="text-[10px] font-mono text-gray-400" data-id="element-6943" data-mp="26w8nux4dplxb">{cls}</code>
            </div>)}
        </div>
      </Card>

      {/* Shadow */}
      <Card className="p-5" data-id="element-6944" data-mp="2gsby6q9dwqvc">
        <div className="flex items-center gap-2 mb-4" data-id="element-6945" data-mp="2gs29d602spr8">
          <Sparkles className="w-4 h-4 text-gray-400" data-id="element-6946" data-mp="2lrom7ebzs3zi" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6947" data-mp="2ltj4ekvvphzw">Shadow-schaal</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 bg-gray-50 rounded-xl p-5" data-id="element-6948" data-mp="2pt0e5qic91av">
          {Object.entries(SHADOW).map(([name, cls]) => <div key={name} className="text-center" data-id="element-6949" data-mp="2zjmph8j0v5nr">
              <div className={`h-16 w-full bg-white rounded-xl ${cls}`} aria-hidden="true" data-id="element-6950" data-mp="2zjuyuqfxrsu7" />
              <p className="mt-2 text-xs font-semibold text-gray-700 capitalize" data-id="element-6951" data-mp="39hiudild94df">
                {name}
              </p>
              <code className="text-[10px] font-mono text-gray-400 break-all" data-id="element-6952" data-mp="3dicm6n1xj3bl">
                {cls}
              </code>
            </div>)}
        </div>
      </Card>

      {/* Badge variants */}
      <Card className="p-5" data-id="element-6953" data-mp="a2grjperjnq0">
        <div className="flex items-center gap-2 mb-4" data-id="element-6954" data-mp="a39pc0vqv2w0">
          <Boxes className="w-4 h-4 text-gray-400" data-id="element-6955" data-mp="nvg8zwy3vs07" />
          <h4 className="text-sm font-semibold text-gray-900" data-id="element-6956" data-mp="nx101cl7ostb">
            Badge-varianten
          </h4>
        </div>
        <div className="flex flex-wrap gap-2.5" data-id="element-6957" data-mp="3s94w9e5wi8v5">
          {Object.entries(BADGE_VARIANTS).map(([name, cls]) => <span key={name} className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`} data-id="element-6958" data-mp="3nal7z73u4kzm">
              {name}
            </span>)}
        </div>
      </Card>
    </div>;
}